/**
 * Background Picker Component
 * Sprint 19: Custom Template Creation
 * 
 * Allows users to configure scene backgrounds using the same component as the recording toolbar
 * with segmented tabs for Colors, Gradients, and Images
 */

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Segmented, Button, Input, Divider, message } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import * as ToggleGroup from '@radix-ui/react-toggle-group';
import { Upload, X } from 'lucide-react';
import { HexColorPicker } from 'react-colorful';
import { SceneBackground } from '../../../../types/template';
import { canvasBackgrounds } from '../../backgrounds/canvasBackgrounds';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { UpgradePrompt } from '../overlays/UpgradePrompt';
import './BackgroundPicker.scss';

interface CustomImage {
  id: string;
  url: string;
}

const MAX_CUSTOM_IMAGES = 3;

interface BackgroundPickerProps {
  background: SceneBackground;
  onChange: (background: SceneBackground) => void;
}

export const BackgroundPicker: React.FC<BackgroundPickerProps> = ({
  background,
  onChange
}) => {
  const getInitialTab = (): string => {
    if (background.type === 'solid') return 'colors';
    if (background.type === 'gradient') return 'gradients';
    if (background.type === 'image') return 'images';
    return 'colors';
  };

  const [activeTab, setActiveTab] = useState<string>(getInitialTab());
  const [customColorPickerOpen, setCustomColorPickerOpen] = useState(false);

  useEffect(() => {
    if (background.type === 'solid') setActiveTab('colors');
    else if (background.type === 'gradient') setActiveTab('gradients');
    else if (background.type === 'image') setActiveTab('images');
  }, [background.type]);
  const [customColor, setCustomColor] = useState('#4A3CFA');
  const [customColorHex, setCustomColorHex] = useState('#4A3CFA');
  const [customImages, setCustomImages] = useState<CustomImage[]>([]);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { isPaid } = useFeatureAccess();

  const solidColors = useMemo(
    () => canvasBackgrounds.filter((bg) => bg.type === 'color'),
    []
  );
  
  const gradients = useMemo(
    () => canvasBackgrounds.filter((bg) => bg.type === 'gradient'),
    []
  );
  
  const images = useMemo(
    () => canvasBackgrounds.filter((bg) => bg.type === 'image'),
    []
  );

  const getSelectedValue = useCallback((): string => {
    if (background.type === 'solid' && background.colors?.[0]) {
      const found = solidColors.find(c => c.color === background.colors![0]);
      if (found) return found.value;
      return `custom-${background.colors[0]}`;
    }
    if (background.type === 'gradient' && background.colors?.[0]) {
      const found = gradients.find(g => g.color === background.colors![0]);
      if (found) return found.value;
    }
    if (background.type === 'image' && background.imageUrl) {
      const found = images.find(i => i.src === background.imageUrl);
      if (found) return found.value;
      const customFound = customImages.find(i => i.url === background.imageUrl);
      if (customFound) return `custom-image-${customFound.id}`;
    }
    return '';
  }, [background, solidColors, gradients, images, customImages]);

  const handleBackgroundChange = useCallback((value: string) => {
    if (!value) return;
    
    const colorBg = solidColors.find(c => c.value === value);
    if (colorBg) {
      onChange({ 
        type: 'solid', 
        colors: [colorBg.color!]
      });
      return;
    }
    
    const gradientBg = gradients.find(g => g.value === value);
    if (gradientBg) {
      onChange({ 
        type: 'gradient', 
        colors: [gradientBg.color!]
      });
      return;
    }
    
    const imageBg = images.find(i => i.value === value);
    if (imageBg) {
      onChange({ 
        type: 'image', 
        imageUrl: imageBg.src
      });
      return;
    }
  }, [solidColors, gradients, images, onChange]);

  const handleCustomColorApply = useCallback(() => {
    const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    if (hexRegex.test(customColorHex)) {
      onChange({ 
        type: 'solid', 
        colors: [customColorHex]
      });
      setCustomColorPickerOpen(false);
      message.success('Custom color applied');
    } else {
      message.error('Please enter a valid hex color');
    }
  }, [customColorHex, onChange]);

  const handleImageUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type - only accept JPEG/JPG
    const allowedTypes = ['image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      message.error('Please select a valid JPEG/JPG image file');
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      message.error('Image size must be less than 5MB');
      return;
    }

    // Check max limit
    if (customImages.length >= MAX_CUSTOM_IMAGES) {
      message.error(`Maximum ${MAX_CUSTOM_IMAGES} custom images allowed`);
      return;
    }

    const imageUrl = URL.createObjectURL(file);
    const imageId = `custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    setCustomImages(prev => [...prev, { id: imageId, url: imageUrl }]);
    onChange({ type: 'image', imageUrl });
    message.success('Custom image uploaded');

    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [onChange, customImages.length]);

  const handleCustomImageSelect = useCallback((imageId: string) => {
    const customImage = customImages.find(img => img.id === imageId);
    if (customImage) {
      onChange({ type: 'image', imageUrl: customImage.url });
    }
  }, [customImages, onChange]);

  const handleCustomImageDelete = useCallback((imageId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const imageToDelete = customImages.find(img => img.id === imageId);
    
    setCustomImages(prev => prev.filter(img => img.id !== imageId));
    
    // If deleted image was selected, reset to first predefined image
    if (imageToDelete && background.imageUrl === imageToDelete.url) {
      const firstImage = images[0];
      if (firstImage) {
        onChange({ type: 'image', imageUrl: firstImage.src });
      }
    }
    
    message.success('Custom image removed');
  }, [customImages, background.imageUrl, images, onChange]);

  const selectedValue = getSelectedValue();

  return (
    <div className="background-picker">
      <h4>Background</h4>
      
      <Segmented
        options={[
          { label: 'Colors', value: 'colors' },
          { label: 'Gradients', value: 'gradients' },
          { label: 'Images', value: 'images' },
        ]}
        block
        size="large"
        value={activeTab}
        onChange={(value) => setActiveTab(value as string)}
        className="background-segmented"
      />

      <div className="background-content">
        {activeTab === 'colors' && (
          <div className="colors-tab">
            <ToggleGroup.Root
              type="single"
              className="background-toggle-group"
              aria-label="Canvas Background"
              value={selectedValue}
              onValueChange={handleBackgroundChange}
            >
              {solidColors.map((bg) => (
                <ToggleGroup.Item
                  key={bg.value}
                  className="background-swatch"
                  value={bg.value}
                  style={{
                    background: bg.color,
                    boxShadow: bg.value === 'white' 
                      ? 'inset 0 0 0 1px #E5E7EB' 
                      : undefined,
                  }}
                  aria-label={bg.label}
                >
                  <span className="selected-indicator">
                    <CheckCircleFilled />
                  </span>
                </ToggleGroup.Item>
              ))}
              <ToggleGroup.Item
                key="custom-color"
                className="background-swatch custom-color-swatch"
                value={selectedValue.startsWith('custom-') ? selectedValue : ''}
                style={{ background: customColor }}
                aria-label="Custom Color"
                onClick={() => setCustomColorPickerOpen(!customColorPickerOpen)}
              >
                <span className="custom-label">Custom</span>
                <span className="selected-indicator">
                  <CheckCircleFilled />
                </span>
              </ToggleGroup.Item>
            </ToggleGroup.Root>

            {customColorPickerOpen && (
              <div className="custom-color-picker">
                <HexColorPicker
                  color={customColor}
                  onChange={(color) => {
                    setCustomColor(color);
                    setCustomColorHex(color);
                  }}
                  style={{ width: '100%' }}
                />
                <div className="color-input-row">
                  <Input
                    value={customColorHex}
                    onChange={(e) => {
                      setCustomColorHex(e.target.value);
                      const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
                      if (hexRegex.test(e.target.value)) {
                        setCustomColor(e.target.value);
                      }
                    }}
                    placeholder="#000000"
                  />
                  <Button type="primary" onClick={handleCustomColorApply}>
                    Apply
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'gradients' && (
          <div className="gradients-tab">
            <ToggleGroup.Root
              type="single"
              className="background-toggle-group"
              aria-label="Canvas Gradient"
              value={selectedValue}
              onValueChange={handleBackgroundChange}
            >
              {gradients.map((bg) => (
                <ToggleGroup.Item
                  key={bg.value}
                  className="background-swatch"
                  value={bg.value}
                  style={{ background: bg.color }}
                  aria-label={bg.label}
                >
                  <span className="selected-indicator">
                    <CheckCircleFilled />
                  </span>
                </ToggleGroup.Item>
              ))}
            </ToggleGroup.Root>
          </div>
        )}

        {activeTab === 'images' && (
          <div className="images-tab">
            <ToggleGroup.Root
              type="single"
              className="background-toggle-group"
              aria-label="Canvas Image"
              value={selectedValue}
              onValueChange={handleBackgroundChange}
            >
              {images.map((bg) => (
                <ToggleGroup.Item
                  key={bg.value}
                  className="background-swatch"
                  value={bg.value}
                  style={{
                    background: `url(${bg.thumbnail || bg.src}) center/cover`,
                  }}
                  aria-label={bg.label}
                >
                  <span className="selected-indicator">
                    <CheckCircleFilled />
                  </span>
                </ToggleGroup.Item>
              ))}
            </ToggleGroup.Root>

            <Divider className="images-divider" />

            <div className="custom-image-section">
              <div className="section-header">
                <span className="section-label">Custom Image</span>
                <span className="section-help">JPG/JPEG • Max 5 MB • Best at 1920×1080</span>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,.jpg,.jpeg"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
              <Button 
                icon={<Upload size={14} />}
                onClick={() => {
                  if (!isPaid) {
                    setShowUpgradePrompt(true);
                    return;
                  }
                  fileInputRef.current?.click();
                }}
                disabled={customImages.length >= MAX_CUSTOM_IMAGES}
              >
                Upload an image
              </Button>
            </div>

            {customImages.length > 0 && (
              <div className="custom-images-grid">
                {customImages.map((img) => (
                  <div
                    key={img.id}
                    className={`custom-image-swatch ${selectedValue === `custom-image-${img.id}` ? 'selected' : ''}`}
                    onClick={() => handleCustomImageSelect(img.id)}
                    style={{
                      backgroundImage: `url(${img.url})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                  >
                    <span className="selected-indicator">
                      <CheckCircleFilled />
                    </span>
                    <button
                      className="delete-button"
                      onClick={(e) => handleCustomImageDelete(img.id, e)}
                      aria-label="Delete custom image"
                    >
                      <X size={10} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      
      <UpgradePrompt
        visible={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        feature="Custom Backgrounds"
        description="Upgrade to Pro to upload your own custom background images for a personalized recording experience."
      />
    </div>
  );
};

export default BackgroundPicker;
